import React from "react";

import {
    SearchResultsState,
    SearchResultsUpdateAction
} from "../interfaces/context/searchResultsContextInterfaces";

type Action = SearchResultsUpdateAction;
type Dispatch = (action: Action) => void;

const SearchResultsStateContext = React.createContext<SearchResultsState | undefined>(undefined);
const SearchResultsDispatchContext = React.createContext<Dispatch | undefined>(undefined);

const searchResultsReducer = (state: SearchResultsState, action: Action) => {
    switch (action.type) {
    case "update_results": {
        return {
            ...state,
            ...action.payload.values
        };
    }
    default: {
        throw new Error(`Unhandled action: ${action}`);
    }
    }
};

/**
 * Search Results Context Provider
 *
 * How to use:
 *
 * Wrap the provider around the components that need access to the context data.
 * ```
 * <SearchResultsProvider>
 *   <Component />
 * </SearchResultsProvider>
 * ```
 * Access the state in <Component />
 * ```
 * const state = useSearchResultsState();
 * ```
 * Call dispatch to update the context state
 * ```
 * const dispatch = useSearchResultsDispatch();
 * dispatch({ type: ACTION_TYPE, payload: ACTION_PAYLOAD });
 * ```
 */
const SearchResultsProvider: React.FC<React.PropsWithChildren<SearchResultsState>> = ({
    children,
    ...initialState
}) => {
    const [state, dispatch] = React.useReducer(searchResultsReducer, initialState);
    return (
        <SearchResultsStateContext.Provider value={state}>
            <SearchResultsDispatchContext.Provider value={dispatch}>
                {children}
            </SearchResultsDispatchContext.Provider>
        </SearchResultsStateContext.Provider>
    );
};

const useSearchResultsState = () => {
    const context = React.useContext(SearchResultsStateContext);
    if (context === undefined) {
        throw new Error("useSearchResultsState must be used within the SearchResultsProvider");
    }
    return context;
};

const useSearchResultsDispatch = () => {
    const context = React.useContext(SearchResultsDispatchContext);
    if (context === undefined) {
        throw new Error("useSearchResultsDispatch must be used within the SearchResultsProvider");
    }
    return context;
};

SearchResultsProvider.displayName = "SearchResultsProvider";
useSearchResultsState.displayName = "useSearchResultsState";
useSearchResultsDispatch.displayName = "useSearchResultsDispatch";

export { SearchResultsProvider, useSearchResultsState, useSearchResultsDispatch };
